<script setup>
/**
 * App-wide host for the profile-completion modal.
 *
 * Mounted once in app.js beside MicrolearningBlocker / AnnouncementModal, so the
 * prompt reaches every Inertia page rather than only /dashboard — a user who
 * signs in through a deep link (proofing notification, StudioMail) lands on
 * `url.intended` and never passes through the dashboard, so an account with no
 * name was never asked for one (SM-9279).
 *
 * Every decision about WHETHER to prompt is the server's (`profileCompletion`
 * shared prop, HandleInertiaRequests::shareProfileCompletion) — guests mid-survey,
 * impersonated sessions and the auth pages are all excluded there. This component
 * only renders what it is told.
 */
import { computed, defineAsyncComponent } from 'vue'
import { usePage } from '@inertiajs/vue3'

const UserMissingFieldsModal = defineAsyncComponent(
    () => import('@/Shared/Modals/UserMissingFieldsModal.vue'),
)

const page = usePage()

const state = computed(() => page.props.profileCompletion ?? null)
const user = computed(() => page.props.user ?? null)
const missingFields = computed(() => state.value?.missing_fields ?? [])
const show = computed(
    () => state.value?.required === true && !!user.value && missingFields.value.length > 0,
)

// The modal opens from its own mounted() hook, so it has to be mounted at the
// moment it becomes required — hence v-if rather than a `show` prop. Keying on
// the field set remounts it if the server's answer changes (e.g. a partial save),
// so `data()` re-seeds the form from the current user instead of stale values.
const modalKey = computed(() => `${user.value?.id ?? 'anon'}:${missingFields.value.join(',')}`)
</script>

<template>
    <UserMissingFieldsModal
        v-if="show"
        :key="modalKey"
        :departments="state.departments ?? []"
        :user="user"
        :missingFields="missingFields"
        :showMissingFieldsPopup="true"
    />
</template>
